import { Component, type ReactNode } from "react";
import { StaleChunkFallback } from "~/components/stale-chunk-fallback";
import { isDynamicImportError, reloadForStaleChunk } from "~/lib/vite-preload-recovery";

type RootErrorBoundaryProps = {
  children: ReactNode;
};

type RootErrorBoundaryState = {
  hasError: boolean;
};

/**
 * Last-resort boundary around the whole Inertia tree. Without it, any uncaught
 * render error (e.g. a page/lazy chunk that failed to load during a transient
 * proxy 502, or a component that threw on partial data mid-blip) unmounts the
 * React root and leaves a blank white screen the user can only escape by
 * reloading by hand. This catches that and either self-heals (a stale-chunk
 * failure reloads once to fresh assets) or shows a recoverable fallback instead
 * of nothing. Complements the loader-level recovery in `vite-preload-recovery`
 * and the SSE-stream recovery in `transmit-context`.
 *
 * Error reporting is handled by `createRoot`'s `onCaughtError`
 * (`Sentry.reactErrorHandler`) so we deliberately do not re-report here.
 */
export class RootErrorBoundary extends Component<RootErrorBoundaryProps, RootErrorBoundaryState> {
  state: RootErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): RootErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: unknown) {
    // A dynamic import that slipped past the loader-level recovery (stale chunk
    // after a deploy, or a chunk fetch that 502'd during a proxy blip): reload
    // once to fresh assets. `reloadForStaleChunk` is guarded by sessionStorage,
    // so it reloads at most once per asset signature and otherwise falls through
    // to the fallback UI below instead of looping.
    if (isDynamicImportError(error)) {
      reloadForStaleChunk();
    }
  }

  render() {
    if (this.state.hasError) {
      return <StaleChunkFallback />;
    }

    return this.props.children;
  }
}
